import { AsyncPipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { Observable, shareReplay } from "rxjs";

@Component({
  selector: "app-central-station-menu",
  imports: [ConfiguredProductComponent, FloorplanProductMenuComponent, AsyncPipe],
  templateUrl: "./central-station-menu.component.html",
  styleUrl: "./central-station-menu.component.scss",
})
export class CentralStationMenuComponent {
  private floorplanService = inject(FloorplanService);
  private dragDropService = inject(FloorplanDragDropService);
  private monitoringProductImageService = inject(MonitoringProductImageService);

  protected floorplan: Floorplan;

  protected readonly tooltipTextCentralStations: string = $localize`:@@global.centralStations:Central Stations`;
  private readonly imageUrlByConfigurationId = new Map<string, Observable<string>>();

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
  }

  protected get centralStations(): readonly CentralStationConfiguration[] {
    if (this.floorplan.project instanceof MonitoringProject) {
      return this.floorplan.project.centralStations;
    }
    return [];
  }

  protected addCentralStation(centralStationConfig: CentralStationConfiguration) {
    this.floorplanService.addCentralStation(centralStationConfig);
  }

  protected addPlaceholder() {
    this.floorplanService.addCentralStationPlaceholder();
  }

  protected deleteCentralStation(centralStationConfig: CentralStationConfiguration) {
    (this.floorplan.project as MonitoringProject).deleteCentralStation(centralStationConfig);
  }

  protected drag(centralStationConfig: CentralStationConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.CENTRAL_STATION, config: centralStationConfig });
  }

  protected getImageUrl$(config: CentralStationConfiguration): Observable<string> {
    const cached = this.imageUrlByConfigurationId.get(config.id);
    if (cached) return cached;

    const cachedImageUrl$ = this.monitoringProductImageService.resolveCentralStationImageUrl(config).pipe(shareReplay(1));

    this.imageUrlByConfigurationId.set(config.id, cachedImageUrl$);
    return cachedImageUrl$;
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.CENTRAL_STATION_PLACEHOLDER });
  }

  protected updateCentralStationsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateCentralStationsItemTextVisibility(visible);
  }
}
